// apps/landing-page/src/lib/catalogPersonalization.ts
// Pure helpers for personalizing the homepage catalog by audience segment +
// program interest. See docs/personalization-spec.md — UX surfaces A + D.

export interface PersonalizableItem {
  id: string;
  examTypes: { slug: string }[];
}

/**
 * Explicit exam-type filter (the catalog chip row). Unlike segment de-noising
 * this is a choice the visitor just made, so it is strict: an item without
 * the selected exam type is dropped, including untyped ones.
 */
export function filterByExamType<T extends PersonalizableItem>(
  items: T[],
  examTypeSlug: string | undefined,
): T[] {
  if (!examTypeSlug) return items;
  return items.filter((item) => item.examTypes.some((examType) => examType.slug === examTypeSlug));
}

/**
 * Segment de-noising: hide items whose exam type is not in the segment's
 * visible set. Items with no exam type stay visible (nothing to de-noise).
 *
 * `visibleSlugs === null` means "no segment set, policy not loaded yet, or
 * the policy fetch failed" — in every one of those cases we fall back to
 * showing everything rather than risk hiding content on a bad state.
 */
export function filterBySegment<T extends PersonalizableItem>(
  items: T[],
  visibleSlugs: Set<string> | null,
  showAll: boolean,
): T[] {
  if (showAll || visibleSlugs === null) return items;
  return items.filter((item) => {
    if (item.examTypes.length === 0) return true;
    return item.examTypes.some((examType) => visibleSlugs.has(examType.slug));
  });
}

/**
 * Sort items matching any of the interest exam types to the top ("any-of"
 * match). Ownership stays the secondary tiebreaker (unowned first) so the
 * existing "don't push what the user already bought" behavior is preserved
 * within each interest group. An empty `interestSlugs` array is equivalent
 * to the old `null` behavior — no personalization, ownership-only sort.
 */
export function sortByInterest<T extends PersonalizableItem>(
  items: T[],
  interestSlugs: string[],
  isOwned: (item: T) => boolean,
): T[] {
  return [...items].sort((a, b) => {
    if (interestSlugs.length > 0) {
      const aMatches = a.examTypes.some((examType) => interestSlugs.includes(examType.slug)) ? 0 : 1;
      const bMatches = b.examTypes.some((examType) => interestSlugs.includes(examType.slug)) ? 0 : 1;
      if (aMatches !== bMatches) return aMatches - bMatches;
    }
    const aOwned = isOwned(a) ? 1 : 0;
    const bOwned = isOwned(b) ? 1 : 0;
    return aOwned - bOwned;
  });
}
